import { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Box, Button } from "@mui/material";

import { getCountriesAndStatesOptions } from "@/utils/forms/getCountriesAndStatesOptions";
import { formConfig } from "./utils/formConfig";
import { formatPhoneNumber } from "@/utils/forms/formatPhoneNumber";
import { signUpDealer } from "@/api/signUpDealer";
import { useGetCountriesAndStatesQuery } from "@/redux-store/api/marketingApi";

import DropdownInput from "@/components/RHFcomponents/DropdownInput";
import Field from "@/components/RHFcomponents/Field";
import Notification from "@/components/Notification/Notification";
import PhoneInput from "@/components/RHFcomponents/PhoneInput";
import Captcha, { CaptchaRef } from "@/components/Captcha/Captcha";
import { useAppDispatch } from "@/redux-store/hooks";
import { setSteperValidation } from "@/redux-store/stepper/stepperSlice";
import { setPartnerOption } from "@/redux-store/dealer/dealerSlice";
import CustomRadioGroup from "@/components/RHFcomponents/CustomRadioGroup";
import { FONT_SECONDARY } from "@/theme/typography";
import Loader from "@/components/Shared/loader";

interface IProps {
  goToNextStep?: () => void;
  onPartnerOptionSelected?: (partnerOption: number) => void;
}

const BecomeDealerForm: React.FC<IProps> = ({ goToNextStep, onPartnerOptionSelected }) => {
  const dispatch = useAppDispatch();
  // Error submit form state
  const [notificationIsOpen, setNotificationIsOpen] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValid, setCaptchaValid] = useState<ICaptchaValid>({
    valid: false,
    value: "",
  });

  const { handleSubmit, control, watch, reset, formState } =
    useForm<IBecomeDealerForm>(formConfig);

  const { data } = useGetCountriesAndStatesQuery({});
  const { countryOptions, stateOptions } = getCountriesAndStatesOptions(data);
  const isUSAselected = watch().idCountry === 1;
  useEffect(() => {
    if (formState.isValid && formSubmitted) {
      dispatch(setSteperValidation(true));
    } else if (!formState.isValid) {
      dispatch(setSteperValidation(false));
    }
  }, [formState.isValid, captchaValid.valid, dispatch, formSubmitted]);
  const onSubmit: SubmitHandler<IBecomeDealerForm> = async (submitData) => {
    if (captchaValid.valid && formState.isValid) {
      setIsSubmitting(true);
      const transformedData: IBecomeDealerForm = {
        ...submitData,
        phoneNumber: formatPhoneNumber(submitData.phoneNumber),
      };
     
      const response = await signUpDealer(transformedData, captchaValid);

      if (response?.status === 200) { 
        setFormSubmitted(true);
        dispatch(setPartnerOption(submitData.partnerOption));
        if (onPartnerOptionSelected) {
          onPartnerOptionSelected(submitData.partnerOption);
        }
        if (goToNextStep) {
          goToNextStep();
        }
        captchaRef.current?.resetCaptcha();
        reset();
      }

      if (response?.status === 400 || response?.status === 500) {
        setNotificationIsOpen(true);
        setError(response.data);
        captchaRef.current?.resetCaptcha();
        setTimeout(() => {
          setNotificationIsOpen(false);
        }, 2500);
      }
      
      setIsSubmitting(false);
    }
  };

  const captchaRef = useRef<CaptchaRef>(null);
  const handleCaptchaVerify = (captcha: ICaptchaValid) => {
    setCaptchaValid(captcha);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        gap={2}
        display="flex"
        flexDirection="column"
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Field
            name="firstName"
            control={control}
            placeholder="Your first name"
          />
          <Field
            name="lastName"
            control={control}
            placeholder="Your last name"
          />
        </Box>

        <Field
          name="companyName"
          control={control}
          placeholder="Company name"
        />
        <Field name="email" control={control} placeholder="Your email" />
        <Field name="username" control={control} placeholder="Username" />
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Field name="password" control={control} placeholder="Password" />
          <Field
            name="confirmPassword"
            control={control}
            placeholder="Re-type password"
          />
        </Box>
        <Field
          name="streetAddress"
          control={control}
          placeholder="Your street name and number"
        />

        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Field name="city" control={control} placeholder="Your city / town" />
          <Field name="zipCode" control={control} placeholder="Your ZIP code" />
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
          sx={{
            "& .MuiFormControl-root:last-of-type": {
              display: isUSAselected ? "block" : "none",
            },
          }}
        >
          {countryOptions.length > 0 && (
            <DropdownInput
              name="idCountry"
              control={control}
              options={countryOptions}
              placeholder="Your country"
            />
          )}
          {stateOptions.length > 0 && (
            <DropdownInput
              name="idState"
              placeholder="Your State"
              control={control}
              options={stateOptions}
            />
          )}
        </Box>

        <PhoneInput
          name="phoneNumber"
          control={control}
          placeholder="Your phone"
        />
        <Box
          fontFamily={FONT_SECONDARY}
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          We offer two flexible ways to partner
        </Box>
        <CustomRadioGroup
          name="partnerOption"
          control={control}
          options={
            [
              {
                id: 1,
                value: 1,
                label: "Standard Installer",
              },
              {
                id: 2,
                value: 2,
                label: "Installer + Seller (Dealer Program)",
              },
            ] as any
          }
        />
        <Captcha onVerify={handleCaptchaVerify} ref={captchaRef} />
        {isSubmitting && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              height: "100vh",
              width: "100%"
            }}
          >
            <Loader style={{ width: 'auto' }} />
          </Box>
        )}
        <Button
          variant="contained"
          type="submit"
          sx={{ mt: 2 }}
          disabled={!captchaValid.valid || !formState.isValid || isSubmitting}
        >
          Sign up
        </Button>
      </Box>

      <Box
        sx={{
          position: "fixed",
          top: "100px",
          left: "60%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          maxWidth: { xs: "calc(100vw - 32px)", md: "500px" },
          width: "100%",
        }}
      >
        <Notification
          open={notificationIsOpen}
          onClose={() => setNotificationIsOpen(false)}
          title="Error"
          description={error}
          severity="error"
        />
      </Box>
    </>
  );
};

export default BecomeDealerForm;
